import * as React from 'react';
import { Locale, defaultLocale, resolveLocale } from './Locale';

/**
 * Custom React hook that makes it easier to work with the dynamically loaded string table.
 *
 * @param getStringTable String table fetcher callback which returns a string table of type T.
 * @param locale Locale of strings to fetch. This helper uses default locale if locale is not provided or unsupported locale is provided.
 * @param logLoadFail Telemetry logger callback for when table fetch fails and getStringTable is falling back to explicit attempt to use default locale.
 */
export function useStringTable<T>(
  getStringTable: (locale: Locale) => Promise<T>,
  locale: string = defaultLocale,
  logLoadFail?: (error: any) => void
) {
  // We need to download the strings for the right language before rendering them to the user.
  // TODO: APP: Determine a better pattern going forward for ensuring strings are loaded. React Suspense data fetching API may be a good fit long term, once that is released.
  const [stringTable, setStringTable] = React.useState<T | undefined>(undefined);
  // Load the strings when the component mounts and re-render with the correct strings.
  React.useEffect(() => {
    let cancelImport = false;

    getStringTable(resolveLocale(locale))
      .then((table) => {
        !cancelImport && setStringTable(table);
      })
      .catch(() => {
        logLoadFail && logLoadFail(new Error(`Get string table failed. Provided locale (${locale}) is unsupported`));

        // If locale is unsupported, then fetch strings for the default locale
        // eslint-disable-next-line @typescript-eslint/no-floating-promises
        getStringTable(defaultLocale)
          .then((table) => {
            !cancelImport && setStringTable(table);
          })
          .catch(() => {
            logLoadFail &&
              logLoadFail(new Error(`Get string table with default locale failed. defaultLocale=&${defaultLocale}`));
          });
      });

    return () => {
      cancelImport = true;
    };
    // TODO: review for possible missing hook deps or document rationale for keeping eslint disable
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return stringTable;
}
